// Parses casino account-statement remarks/descriptions into a canonical game
// type + round id so the row can open the casino result modal.
//
// Mirrors d99-frontend/src/components/casino/common/casinoRemark.js. The server
// (StatementBuilder.js) builds the remark as "<Game> / R.No : <roundId> / <sel>"
// using display names that line up with CASINO_TYPES below, so the reverse
// lookup here resolves them back to the game code getDetailResults() expects.

// value -> label list, kept in sync with the frontend CASINO_TYPES.
export const CASINO_TYPES = [
  { value: "teen", label: "Teenpatti 1-day" },
  { value: "poker", label: "Poker 1-Day" },
  { value: "3cardj", label: "3 Cards Judgement" },
  { value: "aaa", label: "Amar Akbar Anthony" },
  { value: "ab20", label: "Andar Bahar" },
  { value: "abj", label: "Andar Bahar 2" },
  { value: "baccarat", label: "Baccarat" },
  { value: "baccarat2", label: "Baccarat 2" },
  { value: "btable", label: "Bollywood Casino" },
  { value: "card32", label: "32 Cards A" },
  { value: "card32eu", label: "32 Cards B" },
  { value: "cmatch20", label: "Cricket Match 20-20" },
  { value: "cmeter", label: "Casino Meter" },
  { value: "dt20", label: "20-20 Dragon Tiger" },
  { value: "dt202", label: "20-20 Dragon Tiger 2" },
  { value: "dt6", label: "1 Day Dragon Tiger" },
  { value: "dtl20", label: "20-20 D T L" },
  { value: "lucky7", label: "Lucky 7 - A" },
  { value: "lucky7eu", label: "Lucky 7 - B" },
  { value: "lucky7eu2", label: "Lucky 7 - C" },
  { value: "poker20", label: "20-20 Poker" },
  { value: "poker6", label: "Poker 6 Players" },
  { value: "teen20", label: "20-20 Teenpatti" },
  { value: "teen8", label: "Teenpatti Open" },
  { value: "teen9", label: "Teenpatti Test" },
  { value: "war", label: "Casino War" },
  { value: "worli", label: "Worli Matka" },
  { value: "worli2", label: "Instant Worli" },
  { value: "worli3", label: "Matka Market" },
  { value: "teen6", label: "Teenpatti - 2.0" },
  { value: "teen62", label: "V VIP Teenpatti 1-day" },
  { value: "queen", label: "Queen" },
  { value: "race20", label: "Race20" },
  { value: "trap", label: "The Trap" },
  { value: "patti2", label: "2 Cards Teenpatti" },
  { value: "teensin", label: "29Card Baccarat" },
  { value: "teenmuf", label: "Muflis Teenpatti" },
  { value: "race17", label: "Race to 17" },
  { value: "trio", label: "Trio" },
  { value: "notenum", label: "Note Number" },
  { value: "teen120", label: "1 CARD 20-20" },
  { value: "teen1", label: "1 CARD ONE-DAY" },
  { value: "ab3", label: "ANDAR BAHAR 50 CARDS" },
  { value: "aaa2", label: "Amar Akbar Anthony 2" },
  { value: "race2", label: "Race to 2nd" },
  { value: "teen3", label: "Instant Teenpatti" },
  { value: "cmeter1", label: "1 Card Meter" },
  { value: "sicbo", label: "Sic Bo" },
  { value: "ballbyball", label: "Ball By Ball" },
  { value: "sicbo2", label: "Sic Bo 2" },
  { value: "teen41", label: "Queen top open teenpatti" },
  { value: "lucky15", label: "Lucky 15" },
  { value: "goal", label: "Goal" },
  { value: "ab4", label: "ANDAR BAHAR 150 CARDS" },
  { value: "ab20", label: "Andar Bahar" },
  { value: "superover", label: "Super Over" },
  { value: "superover3", label: "Mini Super Over" },
  { value: "ourroullete", label: "Unique Roulette" },
  { value: "mogambo", label: "Mogambo" },
  { value: "dolidana", label: "Dolidana" },
  { value: "lucky5", label: "Lucky 6" },
  { value: "teenunique", label: "Unique Teenpatti" },
  { value: "joker1", label: "Unlimited Joker One Day" },
  { value: "joker120", label: "Unlimited Joker 20-20" },
  { value: "poison", label: "Teenpatti Poison One Day" },
  { value: "poison20", label: "Teenpatti Poison 20-20" },
  { value: "lottcard", label: "Lottery" },
  { value: "cricketv3", label: "Five Five Cricket" },
];

const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Resolve a loose game token (value OR label) from a statement remark into the
 * canonical gameType value used by getDetailResults / CasinoResultBodyByGameType.
 *   "trap"      -> "trap"
 *   "The Trap"  -> "trap"
 */
export function resolveCasinoGameType(token) {
  if (!token) return "";
  const t = String(token).trim().toLowerCase();
  const byValue = CASINO_TYPES.find((g) => g.value.toLowerCase() === t);
  if (byValue) return byValue.value;
  const byLabel = CASINO_TYPES.find((g) => g.label.toLowerCase() === t);
  if (byLabel) return byLabel.value;
  const n = norm(t);
  const byNorm = CASINO_TYPES.find((g) => norm(g.value) === n || norm(g.label) === n);
  if (byNorm) return byNorm.value;
  return t; // already a raw value like "trap" (game not in CASINO_TYPES)
}

/** Human-friendly label for a gameType value. */
export function casinoGameLabel(gameType) {
  const found = CASINO_TYPES.find((g) => g.value === gameType);
  return found ? found.label : gameType;
}

/**
 * Parse a casino account-statement remark/description into its parts.
 *   "The Trap / R.No : 135260605163327 / Player A"
 *     -> { gameType: "trap", mid: "135260605163327", label: "The Trap", raw: "The Trap" }
 * Returns null when there is no round id (i.e. the row isn't a casino bet).
 */
export function parseCasinoRemark(description) {
  if (!description) return null;
  const roundMatch = String(description).match(/R\.?\s*No\.?\s*[:-]?\s*([0-9]+)/i);
  const mid = roundMatch ? roundMatch[1] : "";
  if (!mid) return null;
  const namePart = String(description).split("/")[0].trim();
  const gameType = resolveCasinoGameType(namePart);
  return { gameType, mid, label: casinoGameLabel(gameType), raw: namePart };
}
